import type { AgentModelOption } from '../services/projects.api';

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const EFFORT_NAMES: Record<string, string> = {
  none: 'sin razonamiento',
  minimal: 'razonamiento mínimo',
  low: 'razonamiento bajo',
  medium: 'razonamiento medio',
  high: 'razonamiento alto',
};

const capitalize = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/** "openai/gpt-5-low" → "GPT-5 · razonamiento bajo"; "gpt-4.1-2025-04-14" → "GPT-4.1". */
const formatModelId = (modelId: string): string => {
  const modelName = modelId.replace(/^[^/]+\//, '').replace(/-\d{4}-\d{2}-\d{2}$/, '');
  const effortMatch = modelName.match(/^(.*)-(none|minimal|low|medium|high)$/);
  const parts = (effortMatch?.[1] ?? modelName).split('-');
  const [family, ...rest] = parts[0] === 'gpt' ? [`GPT-${parts[1]}`, ...parts.slice(2)] : parts.map(capitalize);
  const base = [family, ...rest.map(capitalize)].join(' ');
  return effortMatch ? `${base} · ${EFFORT_NAMES[effortMatch[2]]}` : base;
};

/**
 * Display name of a model. Models in the backoffice list have a curated name; any other model (an old project model,
 * or the model of an old version) gets a name built from its id. The API sends the raw id as the name of a
 * project model that is not in the list.
 */
export const getAgentModelName = (modelId: string, options: AgentModelOption[] | undefined): string => {
  const option = options?.find((candidate) => candidate.id === modelId);
  const rawName = modelId.replace(/^[^/]+\//, '');
  return option && option.name !== rawName ? option.name : formatModelId(modelId);
};

/** Shorter name for tight spaces: "GPT-6 Luna · bajo". */
export const getAgentModelShortName = (modelId: string, options: AgentModelOption[] | undefined): string =>
  getAgentModelName(modelId, options).replace(' · razonamiento ', ' · ');

/** "$0.20 entrada · $1.20 salida", or null when the model has no price. */
export const formatAgentModelPrice = (option: AgentModelOption): string | null => {
  if (option.inputPricePerMillion === null || option.outputPricePerMillion === null) return null;
  return `${PRICE_FORMATTER.format(option.inputPricePerMillion)} entrada · ${PRICE_FORMATTER.format(
    option.outputPricePerMillion
  )} salida`;
};

export type PriceComparison = 'cheaper' | 'more-expensive' | null;

/** Compares both prices with the production model. Null when they go in different directions or are equal. */
export const comparePriceWith = (option: AgentModelOption, current: AgentModelOption | undefined): PriceComparison => {
  if (!current || option.id === current.id) return null;
  const { inputPricePerMillion: input, outputPricePerMillion: output } = option;
  const { inputPricePerMillion: currentInput, outputPricePerMillion: currentOutput } = current;
  if (input === null || output === null || currentInput === null || currentOutput === null) return null;
  if (input <= currentInput && output <= currentOutput && (input < currentInput || output < currentOutput)) {
    return 'cheaper';
  }
  if (input >= currentInput && output >= currentOutput && (input > currentInput || output > currentOutput)) {
    return 'more-expensive';
  }
  return null;
};
